import React, { createContext, useContext, useEffect, useState } from 'react';
import { DESKTOP_SCALE, DesktopScale, WIDE_DESKTOP_SCALES } from './constants';

// One breakpoint for the whole app, computed once in a context instead of a resize
// listener per component. Phones and tablets keep the mobile shell (top bar + bottom nav);
// the desktop shell (right sidebar) starts at DESKTOP_MIN_WIDTH.

export type Breakpoint = 'mobile' | 'tablet' | 'desktop';

export const TABLET_MIN_WIDTH = 768;
export const DESKTOP_MIN_WIDTH = 1100;

export interface BreakpointInfo {
  bp: Breakpoint;
  isMobile: boolean;
  isDesktop: boolean;
  // Legacy meaning: true from 768px up, desktop included. Existing callers use it for
  // their wider-column layouts; new code should test `bp` instead.
  isTablet: boolean;
}

const TABLET_QUERY = `(min-width: ${TABLET_MIN_WIDTH}px)`;
const DESKTOP_QUERY = `(min-width: ${DESKTOP_MIN_WIDTH}px)`;

const makeInfo = (bp: Breakpoint): BreakpointInfo => ({
  bp,
  isMobile: bp === 'mobile',
  isDesktop: bp === 'desktop',
  isTablet: bp !== 'mobile',
});

// Stable objects, so the context value only changes when the breakpoint does
const INFO: Record<Breakpoint, BreakpointInfo> = {
  mobile: makeInfo('mobile'),
  tablet: makeInfo('tablet'),
  desktop: makeInfo('desktop'),
};

const readBreakpoint = (): Breakpoint => {
  if (typeof window === 'undefined') return 'mobile';
  if (typeof window.matchMedia !== 'function') {
    const w = window.innerWidth;
    return w >= DESKTOP_MIN_WIDTH ? 'desktop' : w >= TABLET_MIN_WIDTH ? 'tablet' : 'mobile';
  }
  if (window.matchMedia(DESKTOP_QUERY).matches) return 'desktop';
  if (window.matchMedia(TABLET_QUERY).matches) return 'tablet';
  return 'mobile';
};

// Index into WIDE_DESKTOP_SCALES, or -1 for the base desktop scale
const readScaleIndex = (): number => {
  if (typeof window === 'undefined') return -1;
  return WIDE_DESKTOP_SCALES.findIndex(step => typeof window.matchMedia === 'function'
    ? window.matchMedia(`(min-width: ${step.minWidth}px)`).matches
    : window.innerWidth >= step.minWidth);
};

const scaleAt = (index: number): DesktopScale => (index >= 0 ? WIDE_DESKTOP_SCALES[index].scale : DESKTOP_SCALE);

const BreakpointContext = createContext<BreakpointInfo | null>(null);
const ScaleContext = createContext<DesktopScale | null>(null);

export const BreakpointProvider = ({ children }: { children: React.ReactNode }) => {
  const [bp, setBp] = useState<Breakpoint>(readBreakpoint);
  const [scaleIndex, setScaleIndex] = useState<number>(readScaleIndex);

  useEffect(() => {
    const update = () => {
      setBp(readBreakpoint());
      setScaleIndex(readScaleIndex());
    };
    if (typeof window.matchMedia !== 'function') {
      window.addEventListener('resize', update);
      return () => window.removeEventListener('resize', update);
    }
    const lists = [TABLET_QUERY, DESKTOP_QUERY]
      .concat(WIDE_DESKTOP_SCALES.map(step => `(min-width: ${step.minWidth}px)`))
      .map(query => window.matchMedia(query));
    for (const list of lists) {
      if (typeof list.addEventListener === 'function') list.addEventListener('change', update);
      else list.addListener(update); // Safari < 14
    }
    update(); // the width may have changed between the first render and this effect
    return () => {
      for (const list of lists) {
        if (typeof list.removeEventListener === 'function') list.removeEventListener('change', update);
        else list.removeListener(update);
      }
    };
  }, []);

  return (
    <BreakpointContext.Provider value={INFO[bp]}>
      <ScaleContext.Provider value={scaleAt(scaleIndex)}>{children}</ScaleContext.Provider>
    </BreakpointContext.Provider>
  );
};

export const useBreakpoint = (): BreakpointInfo => {
  const ctx = useContext(BreakpointContext);
  // Outside the provider (e.g. an isolated test render): a one-off reading, no updates
  return ctx || INFO[readBreakpoint()];
};

// How much larger the desktop shell renders the page area and the sidebar at the current
// screen width (see layout/constants). Only the desktop shell applies it.
export const useDesktopScale = (): DesktopScale => {
  const ctx = useContext(ScaleContext);
  return ctx || scaleAt(readScaleIndex());
};
